import React, { useState, useEffect } from "react";
import Sidebar from "./components/Sidebar";
import MobileHeader from "./components/MobileHeader";
import HomeSection from "./sections/HomeSection";
import AboutSection from "./sections/AboutSection";
import SkillsSection from "./sections/SkillsSection";
import ExperienceSection from "./sections/ExperienceSection";
import ProjectsSection from "./sections/ProjectsSection";
import EducationSection from "./sections/EducationSection";
import ResumeSection from "./sections/ResumeSection";
import type { SectionKey } from "./lib/types";

function App() {
  const [activeSection, setActiveSection] = useState<SectionKey>("home");
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const navigate = (section: SectionKey) => {
    setActiveSection(section);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderSection = (): React.ReactNode => {
    switch (activeSection) {
      case "home":
        return <HomeSection activeSection={activeSection} onNavigate={navigate} />;
      case "about":
        return <AboutSection />;
      case "skills":
        return <SkillsSection />;
      case "experience":
        return <ExperienceSection />;
      case "projects":
        return <ProjectsSection />;
      case "education":
        return <EducationSection />;
      case "resume":
        return <ResumeSection />;
      default:
        return <HomeSection activeSection={activeSection} onNavigate={navigate} />;
    }
  };

  return (
    <div
      className={[
        "min-h-screen bg-bg font-sans text-content flex transition-opacity duration-300",
        mounted ? "opacity-100" : "opacity-0",
      ].join(" ")}
    >
      {/* Mobile header */}
      <MobileHeader
        sidebarOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Sidebar */}
      <Sidebar
        activeSection={activeSection}
        onNavigate={navigate}
        open={sidebarOpen}
      />

      {/* Mobile overlay */}
      {sidebarOpen ? (
        <div
          onClick={() => setSidebarOpen(false)}
          className="hidden max-md:block fixed inset-0 bg-black/60 z-[80]"
        />
      ) : null}

      {/* Main content */}
      <main className="flex-1 min-w-0 px-5 sm:px-8 lg:px-14 py-10 max-w-[860px] mx-auto max-md:pt-20">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 mb-8 text-xs font-mono text-content-muted">
          <span
            className="cursor-pointer transition-colors duration-150 hover:text-content"
            onClick={() => navigate("home")}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") navigate("home");
            }}
          >
            ~
          </span>
          <span className="opacity-40">/</span>
          <span className="text-accent">{activeSection}</span>
        </div>

        {/* Section content — key forces re-mount for animation */}
        <div key={activeSection}>{renderSection()}</div>

        {/* Footer */}
        <div className="mt-16 pt-6 border-t border-border flex justify-between flex-wrap gap-3 text-2xs text-content-muted font-mono">
          <span>built with intention</span>
          <span>tab to autocomplete · type help</span>
        </div>
      </main>
    </div>
  );
}

export default App;